// DashboardAdmin.jsx
import React, { useState, useEffect, useContext } from "react";
import { AuthContext } from "../../context/AuthContext";
import api from "../../utils/Api";
import { Users, Clock, AlertCircle, Bell } from "lucide-react";

export default function DashboardAdmin() {
  const { token, loading } = useContext(AuthContext);

  const [resumen, setResumen] = useState({
    fichajesHoy: 0,
    incidenciasPendientes: 0,
    totalEmpleados: 0,
    ultimaIncidencia: null,
  });

  const [incidencias, setIncidencias] = useState([]);
  const [filterEstado, setFilterEstado] = useState("");

  const loadData = async () => {
    if (!token) return;

    try {
      const hoy = new Date().toISOString().slice(0, 10);

      // Obtener fichajes, incidencias y usuarios
      const [fichajesRes, incidenciasRes, empleadosRes] = await Promise.all([
        api.get("/fichajes", { headers: { Authorization: `Bearer ${token}` }, params: { fecha_inicio: hoy } }),
        api.get("/incidencias", { headers: { Authorization: `Bearer ${token}` } }),
        api.get("/usuarios", { headers: { Authorization: `Bearer ${token}` } }),
      ]);

      const fichajesArray = Array.isArray(fichajesRes.data.data) ? fichajesRes.data.data : [];
      const incidenciasArray = Array.isArray(incidenciasRes.data) ? incidenciasRes.data : [];
      const empleadosArray = Array.isArray(empleadosRes.data) ? empleadosRes.data : [];

      const pendientes = incidenciasArray.filter(i => i.estado === "pendiente");
      const ultima = incidenciasArray.sort((a, b) => new Date(b.fecha_hora) - new Date(a.fecha_hora))[0] || null;

      setResumen({
        fichajesHoy: fichajesArray.length,
        incidenciasPendientes: pendientes.length,
        totalEmpleados: empleadosArray.length,
        ultimaIncidencia: ultima,
      });

      setIncidencias(incidenciasArray);
    } catch (err) {
      console.error("Error cargando datos:", err);
    }
  };

  useEffect(() => { loadData(); }, [token]);

  if (loading) return <p className="text-center mt-5">Cargando...</p>;

  const incidenciasFiltradas = filterEstado ? incidencias.filter(i => i.estado === filterEstado) : incidencias;

  return (
    <div className="container-fluid">
      <h1 className="fs-3 fw-bold">Dashboard Administración</h1>

      {/* KPI CARDS */}
      <div className="row g-3 mb-4">
        {[
          { title: "Usuarios", value: resumen.totalEmpleados, icon: <Users size={28} />, bg: "linear-gradient(135deg, #FF7A00, #FFB347)", textColor: "white" },
          { title: "Fichajes Hoy", value: resumen.fichajesHoy, icon: <Clock size={28} />, bg: "linear-gradient(135deg, #2C2C2C, #555)", textColor: "white" },
          { title: "Incidencias Pend.", value: resumen.incidenciasPendientes, icon: <AlertCircle size={28} />, bg: "#FFC107", textColor: "#2C2C2C" },
          { title: "Última Incidencia", value: resumen.ultimaIncidencia?.comentario || "Sin incidencias", icon: <Bell size={28} />, bg: "#f5f5f5", textColor: "#2C2C2C" },
        ].map((card, idx) => (
          <div key={idx} className="col-12 col-md-3">
            <div className="card shadow-sm rounded-4 p-3 text-center" style={{ background: card.bg, color: card.textColor, minHeight: "130px" }}>
              <div className="d-flex justify-content-center mb-2">{card.icon}</div>
              <h6 style={{ fontSize: "0.9rem" }}>{card.title}</h6>
              <p className="h4 mb-1" style={{ fontWeight: 600 }}>{card.value}</p>
              {card.title === "Última Incidencia" && resumen.ultimaIncidencia && (
                <small className="text-muted">{new Date(resumen.ultimaIncidencia.fecha_hora).toLocaleString()}</small>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* LISTADO DE INCIDENCIAS */}
      <div className="card shadow-sm rounded-4 p-3">
        <h6 className="mb-3">Últimas incidencias</h6>
        <div className="mb-3 d-flex gap-2 align-items-center flex-wrap">
          <label className="fw-bold" style={{ fontSize: "0.85rem" }}>Filtrar por estado:</label>
          <select className="form-select w-auto" value={filterEstado} onChange={e => setFilterEstado(e.target.value)}>
            <option value="">Todos</option>
            <option value="pendiente">Pendiente</option>
            <option value="resuelta">Resuelta</option>
            <option value="rechazada">Rechazada</option>
          </select>
        </div>

        {incidenciasFiltradas.length > 0 ? (
          <div className="row g-3">
            {incidenciasFiltradas.map(i => (
              <div key={i.id} className="col-12 col-md-6 col-lg-4">
                <div className="card shadow-sm rounded-4 p-3 h-100 hover-shadow" style={{ transition: "all 0.3s", cursor: "pointer" }}>
                  <div className="d-flex justify-content-between mb-2">
                    <strong style={{ fontSize: "0.85rem" }}>{i.tipo.toUpperCase()}</strong>
                    <span
                      className={`badge ${
                        i.estado === "pendiente" ? "bg-warning text-dark" :
                        i.estado === "resuelta" ? "bg-success" : "bg-danger"
                      }`}
                      style={{ fontSize: "0.75rem" }}
                    >
                      {i.estado.toUpperCase()}
                    </span>
                  </div>
                  <p className="mb-1" style={{ fontSize: "0.85rem" }}><strong>Empleado:</strong> {i.usuario?.nombre || "Desconocido"}</p>
                  <p className="mb-1 text-truncate" style={{ fontSize: "0.85rem" }}><strong>Comentario:</strong> {i.comentario}</p>
                  <p className="mb-0 text-muted" style={{ fontSize: "0.75rem" }}>{new Date(i.fecha_hora).toLocaleString()}</p>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-center text-muted mt-2" style={{ fontSize: "0.85rem" }}>No hay incidencias</p>
        )}
      </div>
    </div>
  );
}
